import * as React from 'react';
import { makeStyles, Text, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircle12Regular,
  CheckmarkCircleFilled,
  Clock12Regular,
  DismissCircle12Regular,
  DismissCircleFilled,
  ErrorCircle12Regular,
  ErrorCircleFilled,
} from '@fluentui/react-icons';
import type { FormatDateOptions } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import type { Status } from '../modules/verifications';
import { useVerificationStatus } from '../modules/verifications';

interface Props {
  type: 'bv' | 'dv';
}

export function BvDvStatus({ type }: Props) {
  const intl = useIntl();
  const {
    [type]: { status, visible, expirationDate },
  } = useVerificationStatus();
  const classes = useStyles();

  if (!visible) {
    return null;
  }

  const Icon = type === 'bv' ? statusToBvIcon[status] : statusToDvIcon[status];
  const messages = type === 'bv' ? statusMessageBv : statusMessageDv;

  const formattedExpirationDate = expirationDate
    ? intl.formatDate(expirationDate, DateFormatOptions)
    : 'Invalid expiration date';

  return (
    <div className={classes.root}>
      <Text className={classes.status}>
        <Icon className={classes.icon} fontSize="1.4em" color={statusToColor[status]} />
        {intl.formatMessage(messages[status], { date: formattedExpirationDate })}
      </Text>
    </div>
  );
}
const DateFormatOptions: FormatDateOptions = {
  year: 'numeric',
  month: 'long',
  day: '2-digit',
};

const statusMessageBv = defineMessages<Status>({
  'not-verified': { defaultMessage: 'Incorporation needs verification', description: 'Business verification status' },
  'appeal-in-review': { defaultMessage: 'In-review', description: 'Business/domain verification status' },
  'in-review': { defaultMessage: 'In-review', description: 'Business/domain verification status' },
  verified: { defaultMessage: 'Verified (Expires on {date})', description: 'Business verification status' },
  failed: { defaultMessage: 'Verification failed', description: 'Business verification status' },
});

const statusMessageDv = defineMessages<Status>({
  'not-verified': { defaultMessage: 'Domain needs verification', description: 'Domain verification status' },
  'appeal-in-review': { defaultMessage: 'In-review', description: 'Business/domain verification status' },
  'in-review': { defaultMessage: 'In-review', description: 'Business/domain verification status' },
  verified: { defaultMessage: 'Domain verified (Expires on {date})', description: 'Domain verification status' },
  failed: { defaultMessage: 'Domain verification failed', description: 'Domain verification status' },
});

const statusToBvIcon: Record<Status, typeof CheckmarkCircleFilled> = {
  'not-verified': ErrorCircle12Regular,
  'appeal-in-review': ErrorCircle12Regular,
  'in-review': Clock12Regular,
  verified: CheckmarkCircle12Regular,
  failed: DismissCircle12Regular,
};
const statusToDvIcon: Record<Status, typeof CheckmarkCircleFilled> = {
  'not-verified': ErrorCircleFilled,
  'appeal-in-review': ErrorCircleFilled,
  'in-review': ErrorCircleFilled,
  verified: CheckmarkCircleFilled,
  failed: DismissCircleFilled,
};
const statusToColor: Record<Status, typeof tokens.colorBrandBackground> = {
  'not-verified': tokens.colorPaletteYellowForeground1,
  'appeal-in-review': tokens.colorPaletteYellowForeground1,
  'in-review': tokens.colorPaletteYellowForeground1,
  verified: tokens.colorPaletteGreenForeground1,
  failed: tokens.colorPaletteRedForeground1,
};

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
    display: 'inline-flex',
  },
  icon: {
    marginRight: tokens.spacingHorizontalXXS,
  },
  status: {
    alignItems: 'center',
    display: 'inline-flex',
  },
});
